import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Small waveform in the same lavender/blue language as a listening banner. */
function WaveBars(): JSX.Element {
  const bars: Array<{ h: number; fill: string }> = [
    { h: 7, fill: '#C7D2FE' },
    { h: 12, fill: '#818CF8' },
    { h: 8, fill: '#A5B4FC' },
    { h: 16, fill: '#4F6EF7' },
    { h: 11, fill: '#93A4FB' },
    { h: 14, fill: '#6366F1' },
    { h: 6, fill: '#C7D2FE' },
    { h: 13, fill: '#4F6EF7' },
    { h: 9, fill: '#A5B4FC' },
  ];
  const barWidth = 3;
  const gap = 2;
  const height = 18;
  const width = bars.length * barWidth + (bars.length - 1) * gap;
  return (
    <svg
      className="audio-download__wave"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
    >
      {bars.map((bar, index) => (
        <rect
          key={index}
          x={index * (barWidth + gap)}
          y={(height - bar.h) / 2}
          width={barWidth}
          height={bar.h}
          rx="1.5"
          fill={bar.fill}
        />
      ))}
    </svg>
  );
}

/**
 * Download an MP3 for a meeting that already has video in R2.
 * The first click prepares the file (ffmpeg, or a cached Recall MP3) and later clicks download immediately.
 */
export function DownloadAudioButton({
  meetingId,
  audioStored,
}: {
  meetingId: string;
  audioStored?: boolean;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const alive = useRef(true);
  const href = `/api/meetings/${meetingId}/audio`;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  async function onClick(): Promise<void> {
    if (audioStored) {
      window.location.assign(href);
      return;
    }
    setBusy(true);
    setErr(null);
    const deadline = Date.now() + 15 * 60 * 1000;
    try {
      let first = true;
      while (alive.current && Date.now() < deadline) {
        const status = await api.audioStatus(meetingId, { retry: first });
        first = false;
        if (!alive.current) return;
        if (status.status === 'ready') {
          window.location.assign(href);
          return;
        }
        if (status.status === 'failed') {
          setErr(status.message || 'Could not create an MP3 from this recording.');
          return;
        }
        await sleep(2500);
      }
      if (alive.current) setErr('Audio is still preparing. Try again in a moment.');
    } catch (e) {
      if (!alive.current) return;
      setErr(e instanceof Error ? e.message : 'Could not create an MP3 from this recording.');
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  return (
    <span className="audio-download-wrap">
      <button
        type="button"
        className="audio-download"
        onClick={() => void onClick()}
        disabled={busy}
        aria-busy={busy}
        title="Download an MP3 of this meeting. The first download is created from the saved video."
      >
        <WaveBars />
        {busy ? 'Preparing audio…' : 'Download audio'}
      </button>
      {err && <span className="audio-download__error">{err}</span>}
    </span>
  );
}
